import { format, isEqual, isValid, parseISO } from 'date-fns'
import { isValid as isValidIban } from 'iban'
import * as Yup from 'yup'

import { EMPTY_LIST, EMPTY_STRING } from '../../../constants/common'
import {
  CURRENCY_DEFAULT,
  CURRENCY_FORMAT_OPTIONS,
  CURRENCY_SYMBOL_MAP,
} from '../../../constants/currency'
import {
  DATE_API_DATA_FORMAT_SHORT,
  DATE_SHORT_FORMAT,
} from '../../../constants/date'
import { REG_EXP_BIC } from '../../../constants/regExp'
import { isEqualNullable } from '../../../utils/common'
import { formatNumberWithSeparators, parseAmount } from '../../../utils/number'
import { getPhoneDisplayValue } from '../../Phone'

import {
  PROFILE_INFO_MONEY_EMPTY_AMOUNT,
  PROFILE_INFO_MONEY_MAX_AMOUNT,
  PROFILE_INFO_NOTES_FIELD_NAME,
  PROFILE_INFO_NOTES_MAX_LENGTH,
  ProfileInfoFieldType,
} from './ProfileInfoWidget.constants'

// A cleared amount is saved as zero, so it equals an initial `0.00`.
function toInfoMoneyAmountNumber(amount) {
  return amount == null || amount === EMPTY_STRING ? 0 : parseAmount(amount)
}

function isMoneyChanged({ current, initial }) {
  return (
    toInfoMoneyAmountNumber(current?.amount) !==
      toInfoMoneyAmountNumber(initial?.amount) ||
    current?.currency !== initial?.currency
  )
}

function isInfoMoneyWithinMax(value) {
  return (
    !value?.amount || parseAmount(value.amount) <= PROFILE_INFO_MONEY_MAX_AMOUNT
  )
}

function getInfoFieldReadValue({ optionLabelKey, rawValue, type }) {
  switch (type) {
    case ProfileInfoFieldType.SELECT:
      return getInfoSelectLabel({ optionLabelKey, value: rawValue })
    case ProfileInfoFieldType.CURRENCY:
      return formatInfoMoney(rawValue)
    case ProfileInfoFieldType.DATE:
      return formatInfoDate(rawValue)
    case ProfileInfoFieldType.PHONE:
      return getPhoneDisplayValue(rawValue)
    default:
      return rawValue
  }
}

function getInfoFieldInitialValue({ optionLabelKey, rawValue, type }) {
  switch (type) {
    // The date input formats whatever it gets, so an empty string would crash it
    case ProfileInfoFieldType.DATE:
      return getInfoDateValue(rawValue)
    case ProfileInfoFieldType.SELECT:
      return rawValue?.id
        ? {
            label: getInfoSelectLabel({ optionLabelKey, value: rawValue }),
            value: rawValue.id,
          }
        : null
    case ProfileInfoFieldType.CURRENCY:
      return {
        amount:
          rawValue?.amount == null ? EMPTY_STRING : String(rawValue.amount),
        currency: rawValue?.currency ?? CURRENCY_DEFAULT,
      }
    default:
      return rawValue ?? EMPTY_STRING
  }
}

function isInfoWidgetCollapsibleField({ data, field }) {
  return !getInfoFieldReadValue({
    optionLabelKey: field.optionLabelKey,
    rawValue: data?.[field.name],
    type: field.type,
  })
}

function toInfoDateApiValue(value) {
  return value ? format(value, DATE_API_DATA_FORMAT_SHORT) : null
}

export function isInfoSelectField(type) {
  return type === ProfileInfoFieldType.SELECT
}

export function isInfoCurrencyField(type) {
  return type === ProfileInfoFieldType.CURRENCY
}

export function isInfoPhoneField(type) {
  return type === ProfileInfoFieldType.PHONE
}

export function isInfoEmailField(type) {
  return type === ProfileInfoFieldType.EMAIL
}

export function isInfoDateField(type) {
  return type === ProfileInfoFieldType.DATE
}

export function getInfoDateValue(rawValue) {
  const date = rawValue ? parseISO(rawValue) : null

  return date && isValid(date) ? date : null
}

export function formatInfoDate(rawValue) {
  const date = getInfoDateValue(rawValue)

  return date ? format(date, DATE_SHORT_FORMAT) : EMPTY_STRING
}

export function getInfoSelectLabel({ optionLabelKey = 'value', value }) {
  return value?.[optionLabelKey] ?? EMPTY_STRING
}

export function formatInfoMoney(value) {
  if (!value || value.amount == null) {
    return EMPTY_STRING
  }

  const amount = formatNumberWithSeparators(
    value.amount,
    CURRENCY_FORMAT_OPTIONS
  )
  const symbol =
    CURRENCY_SYMBOL_MAP[value.currency] ?? value.currency ?? EMPTY_STRING

  return symbol ? `${amount} ${symbol}` : amount
}

export function focusInfoSelectSearchInputAfterOpen({ inputSelectRef }) {
  inputSelectRef.current?.focus()
}

export function toInfoSelectOptions(items, optionLabelKey = 'value') {
  return (items || EMPTY_LIST).map(item => ({
    label: item[optionLabelKey],
    value: item.id,
  }))
}

export function hasInfoWidgetCollapsibleFields({ config = EMPTY_LIST, data }) {
  return config.some(field => isInfoWidgetCollapsibleField({ data, field }))
}

export function getInfoWidgetReadFields({ config = EMPTY_LIST, data, t }) {
  return config.map(field => {
    const { labelKey, name, optionLabelKey, placeholderKey, type } = field

    return {
      isCollapsible: isInfoWidgetCollapsibleField({ data, field }),
      label: t(labelKey),
      name,
      placeholder: placeholderKey ? t(placeholderKey) : EMPTY_STRING,
      type,
      value: getInfoFieldReadValue({
        optionLabelKey,
        rawValue: data?.[name],
        type,
      }),
    }
  })
}

export function getInfoWidgetInitialValues({ config = EMPTY_LIST, data }) {
  return config.reduce(
    (values, { name, optionLabelKey, type }) => {
      values[name] = getInfoFieldInitialValue({
        optionLabelKey,
        rawValue: data?.[name],
        type,
      })

      return values
    },
    { [PROFILE_INFO_NOTES_FIELD_NAME]: data?.notes ?? EMPTY_STRING }
  )
}

export function getInfoWidgetPayload({
  blockName,
  config = EMPTY_LIST,
  initialValues,
  values,
}) {
  const changed = {}

  if (
    values[PROFILE_INFO_NOTES_FIELD_NAME] !==
    initialValues[PROFILE_INFO_NOTES_FIELD_NAME]
  ) {
    changed[PROFILE_INFO_NOTES_FIELD_NAME] =
      values[PROFILE_INFO_NOTES_FIELD_NAME]
  }

  config.forEach(({ name, type }) => {
    const current = values[name]
    const initial = initialValues[name]

    if (isInfoSelectField(type)) {
      const currentId = current?.value ?? null
      const initialId = initial?.value ?? null

      if (currentId !== initialId) {
        changed[name] = currentId
      }
    } else if (isInfoCurrencyField(type)) {
      const hasAmount =
        current?.amount !== EMPTY_STRING && current?.amount != null
      const hadAmount =
        initial?.amount !== EMPTY_STRING && initial?.amount != null

      if ((hasAmount || hadAmount) && isMoneyChanged({ current, initial })) {
        // The backend rejects a null amount, so a cleared field is saved as
        // zero, like the legacy profile form does.
        changed[name] = {
          amount: hasAmount
            ? String(current.amount)
            : PROFILE_INFO_MONEY_EMPTY_AMOUNT,
          currency: current?.currency ?? CURRENCY_DEFAULT,
        }
      }
    } else if (isInfoDateField(type)) {
      // `initialValues` is rebuilt on every render, so dates must be compared by
      // time and not by reference
      if (!isEqualNullable(current, initial, isEqual)) {
        changed[name] = toInfoDateApiValue(current)
      }
    } else if ((current ?? EMPTY_STRING) !== (initial ?? EMPTY_STRING)) {
      changed[name] = current
    }
  })

  if (Object.keys(changed).length === 0) {
    return null
  }

  // Flat data (no `block_name`, e.g. the provider profile) is PATCHed with the
  // changed fields directly; info blocks stay nested under their block name.
  return blockName ? { [blockName]: changed } : changed
}

export function getInfoWidgetValidationSchema({ config = EMPTY_LIST, t }) {
  const shape = {
    [PROFILE_INFO_NOTES_FIELD_NAME]: Yup.string()
      .ensure()
      .max(PROFILE_INFO_NOTES_MAX_LENGTH),
  }

  config.forEach(({ maxLength, name, type }) => {
    switch (type) {
      case ProfileInfoFieldType.SELECT:
      case ProfileInfoFieldType.DATE:
        shape[name] = Yup.mixed().nullable()
        break
      case ProfileInfoFieldType.CURRENCY:
        shape[name] = Yup.mixed()
          .nullable()
          .test({
            message: t('Error.MaxValueKey', {
              value: formatNumberWithSeparators(
                PROFILE_INFO_MONEY_MAX_AMOUNT,
                CURRENCY_FORMAT_OPTIONS
              ),
            }),
            test: isInfoMoneyWithinMax,
          })
        break
      case ProfileInfoFieldType.EMAIL:
        shape[name] = Yup.string()
          .ensure()
          .email(t('Widget.InfoEmailInvalidKey'))
          .max(maxLength)
        break
      case ProfileInfoFieldType.IBAN:
        shape[name] = Yup.string()
          .ensure()
          .max(maxLength)
          .test({
            message: t('Error.IncorrectIBANKey'),
            test: value => !value || isValidIban(value),
          })
        break
      case ProfileInfoFieldType.BIC:
        shape[name] = Yup.string()
          .ensure()
          .max(maxLength)
          .matches(REG_EXP_BIC, {
            excludeEmptyString: true,
            message: t('Error.IncorrectBICKey'),
          })
        break
      default:
        shape[name] = Yup.string().ensure().max(maxLength)
    }
  })

  return Yup.object(shape)
}
